import './MobileBundlesListPage.scss';

import {
  IonButton,
  IonChip,
  IonContent,
  IonIcon,
  IonicSlides,
  IonLoading,
  IonPage,
  IonSearchbar,
  useIonViewWillEnter,
} from '@ionic/react';
import { close, filter } from 'ionicons/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { selectAuthUserInfo, selectUserFeatureFlags } from 'src/redux/auth/selectors';
import { useSocketGet, useSocketList } from 'src/utils/socket';
import { FreeMode } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { BundlesEmptyOrPaywallCard, FilterBundleProfiles } from '../../components/bundles/BundlesEmptyOrPaywallCard';
import { MobileLogsBundlesTabs } from '../../components/MobileLogsBundlesTabs';
import MobileHeader from '../../components/reusable-components/MobileHeader';
import { BundleMessages } from '../../intl/BundleMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { JobMessages } from '../../intl/JobMessages';
import { ListOrganizationsRequest } from '../../models/organization';
import { BundleSearchRequest, downloadBundleTemplate } from '../../redux/api/bundles';
import { appBarActions } from '../../redux/appBar/actions';
import { useLimitedMode } from '../../utils/auth/useLimitedMode';
import { FeatureGate } from '../../utils/feature-flags/FeatureFlags';
import { SocketContextPortal } from '../../utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { useSocketSearch } from '../../utils/socket/useSocketSearch';
import { BundleListPageActions } from './BundlesListPage';
import { FilterBundlesModal, FilterBundlesModalState } from './FilterBundlesModal';
import { BundlesListInfinite } from './bundles-list-infinite/BundlesListInfinite';
import { useToast } from 'src/components/reusable-components/useToast';

export const FILTER_PROPERTY = {
  ORGANIZATION: 'organizationId',
  SITE_NAME: 'siteName',
};

const MobileBundlesListPage: React.FC = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { isLimited } = useLimitedMode();
  const [present] = useToast();
  const { state: locationState } = useLocation<{ action?: BundleListPageActions }>();
  const history = useHistory();
  /** The state of the currently logged in user. */
  const userData = useSelector(selectAuthUserInfo);

  const [searchText, setSearchText] = useState<string>('');
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const { item: userOrg } = useSocketGet(SocketContextPortal, 'organizations', userData?.org);
  const [filterOptionState, setFilterOptionState] = useState<FilterBundleProfiles>({
    organizationId: undefined,
    siteName: undefined,
  });
  const [orgRequest] = useState<ListOrganizationsRequest>({
    statuses: ['active'],
    pageNumber: 0,
    pageSize: 1000,
    sort: { order: 'asc', property: 'orgName' },
  });
  const { items: organizations } = useSocketList(SocketContextPortal, 'organizations', orgRequest, 'listV2');

  const supportedPageSize = 300;

  const searchRequest = useMemo(() => {
    const request: BundleSearchRequest = {
      organizationId: filterOptionState.organizationId ?? undefined,
      searchTerm: searchText ?? undefined,
      siteName: filterOptionState.siteName ?? undefined,
      pageNumber: 0,
      pageSize: supportedPageSize,
      sort: {
        property: 'createdAt',
        order: 'desc',
      },
    };
    return request;
  }, [filterOptionState.siteName, filterOptionState.organizationId, searchText]);

  const { items: searchResults, isLoading: isBundlesLoading } = useSocketSearch(
    SocketContextSentinel,
    'bundles',
    searchRequest,
  );

  const handleDownload = React.useCallback(async () => {
    await downloadBundleTemplate();
  }, []);

  const isBundlesOn = useSelector(selectUserFeatureFlags)['BUNDLES'];

  const applyFilter = useCallback(
    (filterObject: FilterBundlesModalState) => {
      setFilterModalOpen(false);

      setFilterOptionState({
        organizationId: filterObject.organization,
        siteName: filterObject.siteName,
      });
    },
    [setFilterModalOpen],
  );

  const filterApplied = filterOptionState.organizationId || filterOptionState.siteName;

  /** Formats the filter values based on their property key. */
  const formattedFilterValues = useCallback(
    (filterKey: string, filterValue: string) => {
      switch (filterKey) {
        case FILTER_PROPERTY.ORGANIZATION:
          if (filterValue === userOrg?._id) {
            return userOrg?.orgName ?? formatMessage(GenericMessages.unknownPlaceholder);
          }
          return (
            organizations?.find(o => o?._id === filterValue)?.orgName ??
            formatMessage(GenericMessages.unknownPlaceholder)
          );
        case FILTER_PROPERTY.SITE_NAME:
          return filterValue ?? formatMessage(GenericMessages.unknownPlaceholder);
        default:
          return formatMessage(GenericMessages.unknownPlaceholder);
      }
    },
    [userOrg, formatMessage, organizations],
  );

  useIonViewWillEnter(() => {
    dispatch(appBarActions.setTitle(formatMessage(JobMessages.bundlesTitle)));
    dispatch(appBarActions.showBack(false));
  }, [dispatch, formatMessage]);

  const bundlesSiteNames = React.useMemo<string[]>(() => {
    const list =
      searchResults?.results?.map(bundle => bundle?.siteName ?? undefined).filter((i): i is string => !!i.trim()) ?? [];
    return [...new Set(list)];
  }, [searchResults?.results]);

  // Show deleted successful or failed toast if the action occurred.
  useEffect(() => {
    if (locationState?.action === BundleListPageActions.BundleDeletedSuccessful) {
      history.replace({ state: {} });
      present(
        {
          message: formatMessage(BundleMessages.bundleDeletedSuccessfullyMessage),
        },
        'success',
      );
    } else if (locationState?.action === BundleListPageActions.BundleDeleteFailed) {
      history.replace({ state: {} });
      present(
        {
          message: formatMessage(BundleMessages.bundleDeletionFailedMessage),
        },
        'danger',
      );
    }
  }, [formatMessage, history, locationState?.action, present]);

  return (
    <IonPage>
      <MobileHeader />
      <FeatureGate featureName={'BUNDLES'}>
        <IonLoading isOpen={isBundlesLoading} spinner="circles" />
        <IonContent scrollX={false} scrollY={false} className="mobile-bundles-list">
          <div className="bundles-list-content">
            <div className="bundles-list-fixed page-content">
              <MobileLogsBundlesTabs />
              {!isLimited && (
                <div className="searchbar">
                  <IonSearchbar
                    placeholder={formatMessage(GenericMessages.searchPlaceholder)}
                    value={searchText}
                    onIonChange={e => setSearchText(e.detail.value!)}
                    debounce={1500}
                  />
                  <IonButton onClick={() => setFilterModalOpen(true)} color="dark" fill="clear">
                    <IonIcon icon={filter} />
                  </IonButton>
                </div>
              )}

              {filterApplied ? (
                <div className={isLimited ? 'ion-padding-top' : ''}>
                  <Swiper
                    freeMode={true}
                    spaceBetween={0}
                    observer={true}
                    className="chip-swiper"
                    modules={[FreeMode, IonicSlides]}
                  >
                    {Object.entries(filterOptionState)
                      .filter(([_, filterValue]) => !!filterValue)
                      .map(([filterKey, filterValue]) => {
                        return (
                          <SwiperSlide key={filterKey} className="chip-swiper-slide">
                            <IonChip>
                              {formattedFilterValues(filterKey, filterValue)}
                              {!isLimited && (
                                <IonIcon
                                  color="dark"
                                  onClick={() => setFilterOptionState({ ...filterOptionState, [filterKey]: null })}
                                  icon={close}
                                />
                              )}
                            </IonChip>
                          </SwiperSlide>
                        );
                      })}
                  </Swiper>
                </div>
              ) : null}
            </div>
            <div className="bundles-list-scroll">
              {searchResults?.results?.length && isBundlesOn ? (
                <BundlesListInfinite
                  bundles={searchResults.results ?? []}
                  loadedEntireResults={
                    typeof searchResults.totalCount === 'number' ? searchResults.totalCount <= supportedPageSize : false
                  }
                  totalCount={searchResults.totalCount ?? 0}
                />
              ) : (
                // Only paywall state will be used on web. The table already handles the empty state.
                // The empty state of this component will only be used on mobile platforms.
                <BundlesEmptyOrPaywallCard
                  handleDownload={handleDownload}
                  validPaidLicense={isBundlesOn}
                  bundlesCount={searchResults?.results?.length ?? 0}
                  searchText={searchText}
                  filterOptions={filterOptionState}
                />
              )}
            </div>
          </div>
          <FilterBundlesModal
            organizations={organizations}
            siteNames={bundlesSiteNames}
            isOpen={filterModalOpen}
            onClose={() => setFilterModalOpen(false)}
            onSave={(e: FilterBundlesModalState) => applyFilter(e)}
          />
        </IonContent>
      </FeatureGate>
    </IonPage>
  );
};

export default MobileBundlesListPage;
